import { supabase } from '../lib/supabase'
import { dayMonth } from './clientDates'

/**
 * WHEN A PIECE REACHED THE CLIENT, AND WHEN THE NEXT ONE IS DUE.
 *
 * The client page says *Added 11 October* under a tile rather than marking
 * it NEW. Portal built NEW badges and removed them on 8 Oct 2026 because
 * they were drawn from the viewer's own browser storage — blank on a phone,
 * blank on the second visit from the office, wrong whenever two people
 * shared the link. A client link is read by a whole team; there is no
 * single "them" to track. A date is true for everyone and needs nothing
 * stored in anybody's browser.
 *
 * `client_added_at` is set by a database trigger the first time a row
 * crosses into being visible AND at a status worth opening, so it is
 * stamped once by whatever path put it there and never moved afterwards.
 * Nothing here writes it.
 *
 * Read as its own small query rather than threaded through the project
 * loader: `types/portal.ts` and `utils/projectsSource.ts` are both large
 * enough that a whole-file rewrite risks truncation, and this is two
 * columns the client page alone cares about.
 */

export interface PieceDates {
  /** ISO date this first reached the client, or '' if it has not. */
  added: string
  /** ISO date it is due, or '' when nothing has been promised. */
  due: string
}

export type DateMap = Map<string, PieceDates>

export async function fetchDeliverableDates(projectId: string): Promise<DateMap> {
  const map: DateMap = new Map()
  if (!supabase || !projectId) return map

  const { data, error } = await supabase
    .from('deliverables')
    .select('deliverable_key, client_added_at, due_date')
    .eq('project_id', projectId)

  if (error) {
    console.warn(`[supabase] deliverable dates load failed: ${error.message}`)
    return map
  }

  for (const row of data ?? []) {
    const key = String((row as Record<string, unknown>).deliverable_key ?? '')
    if (!key) continue
    map.set(key, {
      added: String((row as Record<string, unknown>).client_added_at ?? '').slice(0, 10),
      due: String((row as Record<string, unknown>).due_date ?? '').slice(0, 10),
    })
  }
  return map
}

/**
 * The line under a tile: what it is, and when.
 *
 * Only ever says what is known. An unfinished piece with no due date says
 * what it is doing and stops — a promised date nobody agreed is worse than
 * no date, and this page is read by the people the promise would be to.
 */
export function tileLine(state: string, dates: PieceDates | undefined, status: string): string {
  const parts: string[] = []
  if (state) parts.push(state)

  const added = dates?.added ? dayMonth(dates.added) : ''
  const due = dates?.due ? dayMonth(dates.due) : ''

  if ((status === 'Approved' || status === 'Delivered') && added) {
    /* Already said in the state word — the date is the useful half. */
    return `${state} ${added}`.trim()
  }
  if (added) parts.push(`added ${added}`)
  else if (due) parts.push(`due ${due}`)

  return parts.join(' · ')
}

/** The newest thing on the page, for the line under the project name. */
export function latestLine(
  items: { id: string; name: string }[],
  dates: DateMap,
): string {
  let best = { name: '', added: '' }
  for (const item of items) {
    const added = dates.get(item.id)?.added ?? ''
    if (added && added > best.added) best = { name: item.name, added }
  }
  if (!best.added) return ''
  return `Latest · ${best.name} added ${dayMonth(best.added)}`
}
